/**
 * "What the AI knows" (create wizard, plan N5, 2026-10-28): a short summary
 * of the messaging brief, drafted from Settings > Offer, FAQ and objections,
 * so the user only fills in what is missing. Filled answers show a check,
 * missing required ones are highlighted, optional empty ones read "Not set".
 * Clicking a row opens the full interview at that question. The exact prompt
 * and the confirm checkbox stay on the wizard step itself.
 */

import { AlertCircle, Check } from 'lucide-react'
import { REQUIRED_BRIEF_FIELDS, type CampaignBrief, type CampaignBriefTextField } from '@dealcloser/shared'
import { cn } from '@/lib/utils'

type SummaryRow = { readonly field: CampaignBriefTextField | 'faqs'; readonly label: string }

/** The answers that shape messages most, in interview order. */
export const BRIEF_SUMMARY_ROWS: readonly SummaryRow[] = [
  { field: 'what_you_sell', label: 'What you sell' },
  { field: 'who_its_for', label: 'Who it is for' },
  { field: 'outcome', label: 'Result customers get' },
  { field: 'proof', label: 'Proof it may cite' },
  { field: 'differentiator', label: 'Why you' },
  { field: 'pricing', label: 'What it may say about price' },
  { field: 'faqs', label: 'FAQs and objections' },
  { field: 'conversation_goal', label: 'How a good conversation ends' },
  { field: 'tone', label: 'Tone' },
]

/** Row state for the summary: filled, missing (required and empty) or not set (optional and empty). */
export function briefRowState(brief: CampaignBrief, field: SummaryRow['field']): 'filled' | 'missing' | 'empty' {
  if (field === 'faqs') {
    return brief.faqs.some((f) => f.question.trim() && f.answer.trim()) ? 'filled' : 'empty'
  }
  if (brief[field].trim()) return 'filled'
  return REQUIRED_BRIEF_FIELDS.includes(field) ? 'missing' : 'empty'
}

function rowValue(brief: CampaignBrief, field: SummaryRow['field']): string {
  if (field === 'faqs') {
    const n = brief.faqs.filter((f) => f.question.trim() && f.answer.trim()).length
    return n === 1 ? '1 question with its answer' : `${n} questions with their answers`
  }
  return brief[field].trim()
}

export function BriefSummary({
  brief,
  onEdit,
}: {
  readonly brief: CampaignBrief
  /** Open the interview at this question ('faqs' opens the FAQ list). */
  readonly onEdit: (field: SummaryRow['field']) => void
}) {
  return (
    <ul className="divide-y rounded-lg border">
      {BRIEF_SUMMARY_ROWS.map((row) => {
        const state = briefRowState(brief, row.field)
        return (
          <li key={row.field}>
            <button
              type="button"
              onClick={() => onEdit(row.field)}
              className={cn(
                'flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors hover:bg-muted/50',
                state === 'missing' && 'bg-amber-500/10',
              )}
            >
              {state === 'filled' ? (
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" aria-label="Filled in" />
              ) : state === 'missing' ? (
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" aria-label="Required" />
              ) : (
                <span className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              )}
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium">{row.label}</span>
                <span
                  className={cn(
                    'block text-xs',
                    state === 'filled' ? 'line-clamp-2 text-muted-foreground' : 'text-muted-foreground italic',
                    state === 'missing' && 'text-amber-700 dark:text-amber-400 not-italic',
                  )}
                >
                  {state === 'filled' ? rowValue(brief, row.field) : state === 'missing' ? 'Required. Click to fill in.' : 'Not set'}
                </span>
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
